import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import Button from "@mui/material/Button";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import Swal from "sweetalert2";
import { API_BASE } from "config/api";

export const MONTHS = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const rowPropTypes = PropTypes.shape({
  id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  serial: PropTypes.number,
  staffName: PropTypes.string,
  position: PropTypes.string,
  grossSalary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  netSalary: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  period: PropTypes.string,
  status: PropTypes.string,
  originalData: PropTypes.object,
});

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const StaffCell = ({ value, row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {value}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {row.original.position || "No position set"}
    </Typography>
  </Box>
);
StaffCell.propTypes = {
  value: PropTypes.string.isRequired,
  row: PropTypes.shape({ original: rowPropTypes }).isRequired,
};

const FrequencyCell = ({ value }) => (
  <Chip size="small" label={value || "monthly"} sx={{ textTransform: "capitalize" }} />
);
FrequencyCell.propTypes = { value: PropTypes.string };
FrequencyCell.defaultProps = { value: "monthly" };

const MoneyCell = ({ value }) => (
  <Typography variant="button" fontWeight={600}>
    GHS {Number(value || 0).toFixed(2)}
  </Typography>
);
MoneyCell.propTypes = { value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]) };

const StatusCell = ({ value }) => (
  <Chip
    size="small"
    label={value}
    color={value === "paid" ? "success" : "warning"}
    sx={{ textTransform: "capitalize" }}
  />
);
StatusCell.propTypes = { value: PropTypes.string.isRequired };

const ActionsCell = ({ row, handleEdit, handleDelete }) => (
  <Stack direction="row" spacing={1} justifyContent="center">
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Stack>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: rowPropTypes }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
};

export default function usePayrollData() {
  const { user, church } = useAuth();

  const [payrollData, setPayrollData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      { Header: "Staff", accessor: "staffName", align: "left", width: "18%", Cell: StaffCell },
      {
        Header: "Frequency",
        accessor: "payFrequency",
        align: "center",
        width: "10%",
        Cell: FrequencyCell,
      },
      {
        Header: "Period",
        accessor: "period",
        align: "center",
        width: "12%",
        Cell: SerialCell,
      },
      {
        Header: "Gross",
        accessor: "grossSalary",
        align: "right",
        width: "13%",
        Cell: MoneyCell,
      },
      {
        Header: "Net Pay",
        accessor: "netSalary",
        align: "right",
        width: "13%",
        Cell: MoneyCell,
      },
      { Header: "Status", accessor: "status", align: "center", width: "10%", Cell: StatusCell },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "25%",
        Cell: (props) => (
          <ActionsCell {...props} handleEdit={handleEdit} handleDelete={handleDelete} />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
    summary: { total_gross: 0, total_net: 0, count: 0 },
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedPayroll, setSelectedPayroll] = useState(null);
  const [staffUsers, setStaffUsers] = useState([]);

  const handleEdit = (payroll) => {
    setSelectedPayroll(payroll);
    setEditModalOpen(true);
  };

  const handleDelete = (payroll) => {
    setSelectedPayroll(payroll);
    setDeleteModalOpen(true);
  };

  const fetchPayroll = useCallback(async () => {
    try {
      setPayrollData((prev) => ({ ...prev, isLoading: true }));
      if (!user || !church) return;

      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/payroll`, {
        method: "GET",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const result = await response.json();
      const rows = result.data || [];

      setPayrollData((prev) => ({
        ...prev,
        rows: rows.map((p, index) => ({
          id: p.id,
          serial: index + 1,
          user_id: p.user_id,
          staffName: p.staff_name || `${p.staff?.name || "Unnamed staff"}`,
          position: p.position,
          payFrequency: p.pay_frequency || "monthly",
          grossSalary: p.gross_salary,
          allowances: p.allowances,
          deductions: p.deductions,
          netSalary: p.net_salary,
          period: `${MONTHS[p.pay_period_month - 1] || ""} ${p.pay_period_year}`,
          payPeriodMonth: p.pay_period_month,
          payPeriodYear: p.pay_period_year,
          paymentDate: p.payment_date,
          status: p.status,
          notes: p.notes,
          originalData: p,
        })),
        isLoading: false,
        error: null,
        summary: result.meta || { total_gross: 0, total_net: 0, count: 0 },
      }));
    } catch (error) {
      console.error("Fetch payroll failed:", error);
      setPayrollData((prev) => ({ ...prev, isLoading: false, error: error.message }));
    }
  }, [user, church]);

  const fetchStaffUsers = useCallback(async () => {
    if (!church?.id) return;
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;
      const response = await fetch(`${API_BASE}/users`, {
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });
      if (!response.ok) return;
      const result = await response.json();
      setStaffUsers(result.data || []);
    } catch (err) {
      console.error("Failed to load staff users:", err);
    }
  }, [church?.id]);

  const handleEditSubmit = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const url = selectedPayroll.id
        ? `${API_BASE}/payroll/${selectedPayroll.id}`
        : `${API_BASE}/payroll`;
      const method = selectedPayroll.id ? "PUT" : "POST";

      const response = await fetch(url, {
        method,
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          user_id: selectedPayroll.user_id || null,
          staff_name: selectedPayroll.user_id ? null : selectedPayroll.staffName,
          position: selectedPayroll.position,
          pay_frequency: selectedPayroll.payFrequency || "monthly",
          gross_salary: selectedPayroll.grossSalary,
          allowances: selectedPayroll.allowances || 0,
          deductions: selectedPayroll.deductions || 0,
          pay_period_month: selectedPayroll.payPeriodMonth,
          pay_period_year: selectedPayroll.payPeriodYear,
          payment_date: selectedPayroll.paymentDate || null,
          status: selectedPayroll.status || "pending",
          notes: selectedPayroll.notes || "",
        }),
      });

      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        const detail =
          body?.errors && Object.values(body.errors).flat()[0]
            ? Object.values(body.errors).flat()[0]
            : body?.message;
        throw new Error(detail || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: "Payroll entry saved",
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchPayroll();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Payroll save failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error", confirmButtonText: "OK" });
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const token = localStorage.getItem("authToken");
      if (!token) return;

      const response = await fetch(`${API_BASE}/payroll/${selectedPayroll.id}`, {
        method: "DELETE",
        headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
      });

      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      Swal.fire({
        title: "Success!",
        text: "Payroll entry deleted",
        icon: "success",
        confirmButtonText: "OK",
      });

      fetchPayroll();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error", confirmButtonText: "OK" });
    }
  };

  const handleAddPayroll = () => {
    const now = new Date();
    setSelectedPayroll({
      id: null,
      user_id: "",
      staffName: "",
      position: "",
      payFrequency: "monthly",
      grossSalary: "",
      allowances: "",
      deductions: "",
      payPeriodMonth: now.getMonth() + 1,
      payPeriodYear: now.getFullYear(),
      paymentDate: now.toISOString().split("T")[0],
      status: "pending",
      notes: "",
    });
    setEditModalOpen(true);
  };

  const handlePrint = () => window.print();

  useEffect(() => {
    fetchPayroll();
    fetchStaffUsers();
  }, [fetchPayroll, fetchStaffUsers]);

  return {
    ...payrollData,
    staffUsers,
    handleEdit,
    handleDelete,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    selectedPayroll,
    setSelectedPayroll,
    handleEditSubmit,
    handleDeleteConfirm,
    handleAddPayroll,
  };
}
